import reserhubApi from './reserhub-api/reserhubApi';

type StoredCardPaymentMode =
  | {
      kind: 'vaulted';
      vaultCardId: string;
    }
  | {
      kind: 'tokenized';
      basisTheoryTokenId: string;
    }
  | {
      kind: 'tokenized_with_cvv';
      basisTheoryTokenId: string;
    }
  | { kind: 'unavailable' };

interface StoredCard {
  brand: string;
  cardType: 'credit' | 'debit';
  expirationLabel: string;
  holderName: string;
  id: string;
  installmentsAvailable: boolean;
  isExpired: boolean;
  last4: string;
  paymentMode: StoredCardPaymentMode;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readIdentifier(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return readString(value);
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function readBooleanFlag(value: unknown): boolean {
  return value === true || value === 'true';
}

function readMonth(value: unknown): number | undefined {
  const month = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(month) && month >= 1 && month <= 12 ? month : undefined;
}

function readYear(value: unknown): number | undefined {
  const year = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(year) && year >= 2000 && year <= 9999 ? year : undefined;
}

function readField(card: Record<string, unknown>, camelCase: string, snakeCase: string): unknown {
  return card[camelCase] ?? card[snakeCase];
}

function createPaymentMode(card: Record<string, unknown>, cardId: string): StoredCardPaymentMode {
  const processorValue = readField(card, 'cardPaymentProcessor', 'card_payment_processor');
  const processor = isRecord(processorValue) ? processorValue : undefined;
  const storedPaymentMethodId = readIdentifier(
    processor
      ? readField(processor, 'storedPaymentMethodId', 'stored_payment_method_id')
      : undefined,
  );
  const basisTheoryTokenId = readIdentifier(readField(card, 'vaultTokenId', 'vault_token_id'));
  const requiresCvv = readBoolean(
    readField(card, 'requiresCvvForPayment', 'requires_cvv_for_payment'),
  );
  if (requiresCvv === undefined) return { kind: 'unavailable' };

  if (requiresCvv) {
    return basisTheoryTokenId
      ? { kind: 'tokenized_with_cvv', basisTheoryTokenId }
      : { kind: 'unavailable' };
  }

  if (storedPaymentMethodId) {
    return { kind: 'vaulted', vaultCardId: cardId };
  }

  if (basisTheoryTokenId) return { kind: 'tokenized', basisTheoryTokenId };
  return { kind: 'unavailable' };
}

/** Convert one untrusted Reserhub card to the fields Checkout is allowed to consume. */
function normalizeStoredCard(value: unknown, now: Date): StoredCard | undefined {
  if (!isRecord(value)) return undefined;

  const id = readIdentifier(value.id);
  if (!id) return undefined;

  const expirationMonth = readMonth(readField(value, 'expirationMonth', 'expiration_month'));
  const expirationYear = readYear(readField(value, 'expirationYear', 'expiration_year'));
  const expiresAt =
    expirationMonth && expirationYear ? new Date(expirationYear, expirationMonth, 1) : undefined;
  const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const cardTypeValue =
    readString(readField(value, 'cardType', 'card_type')) || readString(value.funding);

  return {
    brand: readString(value.brand),
    cardType: cardTypeValue === 'debit' ? 'debit' : 'credit',
    expirationLabel:
      expirationMonth && expirationYear
        ? `${String(expirationMonth).padStart(2, '0')}/${expirationYear}`
        : '',
    holderName: readString(readField(value, 'holderName', 'holder_name')),
    id,
    installmentsAvailable:
      readBooleanFlag(readField(value, 'msiEligible', 'msi_eligible')) ||
      readBooleanFlag(readField(value, 'installmentsAvailable', 'installments_available')),
    isExpired: expiresAt ? expiresAt <= currentMonth : false,
    last4: readString(value.last4).slice(-4),
    paymentMode: createPaymentMode(value, id),
  };
}

/** Validate and normalize the stored-card collection returned by Reserhub. */
function normalizeStoredCardsResponse(value: unknown, now = new Date()): StoredCard[] {
  if (!isRecord(value) || !Array.isArray(value.cards)) {
    throw new Error('stored_cards_response_invalid');
  }

  return value.cards.flatMap((card) => {
    const normalizedCard = normalizeStoredCard(card, now);
    return normalizedCard ? [normalizedCard] : [];
  });
}

async function fetchStoredCards(): Promise<StoredCard[]> {
  const response: unknown = await reserhubApi.request('/accounts/users/me/stored-cards', {
    method: 'GET',
  });
  return normalizeStoredCardsResponse(response);
}

async function deleteStoredCard(cardId: string): Promise<void> {
  await reserhubApi.request(`/accounts/users/me/stored-cards/${encodeURIComponent(cardId)}`, {
    method: 'DELETE',
  });
}

export type { StoredCard, StoredCardPaymentMode };
export { deleteStoredCard, fetchStoredCards, normalizeStoredCardsResponse };
